import Bugsnag from "@bugsnag/js";
import { AppleSignUp, AppleOauthResponse } from "../../models/Interfaces";
import { getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router-dom";
import { appSettingsAtom, writeOnlyTokensAtom, writeOnlyUserAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { errorLog } from "../../utils/logger";
import { STAY_LOGGED_IN_PW, REDIRECT_URI } from "../../globals";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SignInWithApple, SignInWithAppleOptions } from "@capacitor-community/apple-sign-in";
import React, { useEffect } from "react";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { updateCryptoKeyAndUserAndTokens } from "../../utils/auth";

// This redirect URL is different because it is configured for deep linking back to the app
// after the Apple Oauth flow is completed.
const ANDROID_REDIRECT_URI = "https://docket.care/apple-auth/callback";
const APPLE_CLIENT_ID = "com.foxhallwythe.docket.mobile.apple.signin";

export default function AppleSignIn({
  onError,
  onLogin,
  setLoggingIn,
}: {
  onError: (error: string) => void;
  onLogin: () => Promise<void>;
  setLoggingIn: (value: boolean) => void;
}) {
  const navigate = useNavigate();
  const [appSettings, setAppSettings] = useAtom(appSettingsAtom);
  const [user, setUser] = useAtom(writeOnlyUserAtom);
  const [tokens, setTokens] = useAtom(writeOnlyTokensAtom);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        // Handle Apple Sign In Redirect. This is only the case for Android
        // Due to the fact that the current Capacitor Apple Sign in Plugins
        // do not support Android.
        // Typical URL: https://docket.care/apple-auth/callback#code=1234567890&id_token=1234567890
        if (event.url.includes("apple-auth/callback")) {
          signInWithAppleAndroid(event.url);
        }
      });
    }
  }, []);

  const parseAppleFragment = (url: string) => {
    const fragment = url.split("#")[1]; // Get part after '#'
    if (!fragment) return {};

    const params = new URLSearchParams(fragment);
    const code = params.get("code") || undefined;
    const id_token = params.get("id_token") || undefined;

    return { code, id_token };
  };

  const signInWithAppleDocket = async (appleSignup: AppleSignUp) => {
    try {
      setLoggingIn(true);
      const loginResult = await getAPIClient().loginApple(appleSignup);

      if (!loginResult || !loginResult.tokens?.access) {
        // This really shouldn't happen
        const err = new Error("Did not get a login result or token");
        Bugsnag.notify(err);
        throw err;
      }

      // oauth users don't have a password....
      await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
      await setAppSettings({
        ...appSettings,
        email: loginResult.email || "",
      });
      await onLogin();
      // First param is the user ID, which we don't expose
      Bugsnag.setUser("", loginResult.email);
      if (
        !loginResult.dob ||
        !loginResult.legal_sex ||
        !loginResult.first_name ||
        !loginResult.last_name
      ) {
        navigate("/signup/accountinfo");
      } else {
        navigate("/home/search");
      }
    } catch (e: any) {
      setLoggingIn(false);
      errorLog(e);
      onError("Apple Sign In failed");
    }
  };

  const signInWithAppleAndroid = async (url: string) => {
    const { code, id_token } = parseAppleFragment(url);
    const appleSignup: AppleSignUp = {
      code: code || "",
      token: id_token || "",
      email: "noEmail@apple.com",
      first_name: "firstName",
      last_name: "lastName",
    };

    if (!code || !id_token) {
      // Either Apple OAuth is not formatting uri correctly or parsing logic is incorrect.
      Bugsnag.notify(new Error("No code or id_token found in Apple Sign In"));
    }

    await signInWithAppleDocket(appleSignup);
  };

  const signInWithAppleIOSWeb = async () => {
    let options: SignInWithAppleOptions = {
      clientId: APPLE_CLIENT_ID,
      redirectURI: REDIRECT_URI,
      scopes: "email name",
    };
    const response = await SignInWithApple.authorize(options);
    const appleSignup: AppleSignUp = {
      code: response.response.authorizationCode,
      token: response.response.identityToken,
      email: response.response.email ?? "noEmail@apple.com",
      first_name: response.response.givenName ?? "firstName",
      last_name: response.response.familyName ?? "lastName",
    };
    await signInWithAppleDocket(appleSignup);
  };

  const handleAppleLoginClick = async () => {
    const platform = Capacitor.getPlatform();
    if (platform === "ios" || platform === "web") {
      await signInWithAppleIOSWeb();
    } else if (platform === "android") {
      // Need to manually trigger the Oauth flow and handle the auth tokens via the event listener
      // to initiate token exchange.
      await Browser.open({
        url: `https://appleid.apple.com/auth/authorize?client_id=${APPLE_CLIENT_ID}&redirect_uri=${ANDROID_REDIRECT_URI}&response_type=code%20id_token&response_mode=fragment`,
      });
    }
  };

  return (
    <button
      data-testid="Sign-in-Apple"
      type="button"
      onClick={handleAppleLoginClick}
      className="btn btn-lg bg-black text-white border-black"
    >
      <svg
        aria-label="Apple logo"
        width="24"
        height="24"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1195 1195"
      >
        <path
          fill="white"
          d="M1006.933 812.8c-32 153.6-115.2 211.2-147.2 249.6-32 25.6-121.6 25.6-153.6 6.4-38.4-25.6-134.4-25.6-166.4 0-44.8 32-115.2 19.2-128 12.8-256-179.2-352-716.8 12.8-774.4 64-12.8 134.4 32 134.4 32 51.2 25.6 70.4 12.8 115.2-6.4 96-44.8 243.2-44.8 313.6 76.8-147.2 96-153.6 294.4 19.2 403.2zM802.133 64c12.8 70.4-64 224-204.8 230.4-12.8-38.4 32-217.6 204.8-230.4z"
        ></path>
      </svg>
      Apple
    </button>
  );
}
